import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/hooks/useAuth';
import { useAppsT } from '@/lib/appsI18n';
import AppShell from '@/pages/client/apps/components/AppShell';
import ShareItemModal from '@/components/feature/ShareItemModal';
import { useAppData, localId, localSet, formatDateTime, downloadTextFile } from '@/lib/appStorage';
import {
  EMPTY_NOTES,
  createNote,
  createNotebook,
  periodIcon,
  summarizeNotes,
  splitSentences,
  NOTEBOOK_COLORS,
  type Note,
  type NotesState,
  type PeriodType,
} from './notesUtils';

export default function ZifekNotesPage() {
  const { user } = useAuth();
  const { t, isRtl } = useAppsT();
  const navigate = useNavigate();
  // Clé de stockage v2 : les anciennes données de démonstration sont ignorées.
  const storage = useAppData<NotesState>('client_notes_v2', EMPTY_NOTES, { scope: 'user', userId: user?.id });
  const { data, setData, ready } = storage;

  const [activeNotebookId, setActiveNotebookId] = useState<string>('');
  const [activeNoteId, setActiveNoteId] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const [filter, setFilter] = useState<'all' | 'favorite' | 'important' | 'review'>('all');
  const [selectionMode, setSelectionMode] = useState(false);
  const [selectedNoteIds, setSelectedNoteIds] = useState<string[]>([]);
  const [showClosed, setShowClosed] = useState(false);

  const [showNotebookForm, setShowNotebookForm] = useState(false);
  const [draftNotebook, setDraftNotebook] = useState({
    name: '',
    subject: '',
    year: '',
    periodType: 'semester' as PeriodType,
    periodLabel: t('notes.periodSemester') + ' 1',
  });

  const [aiPanel, setAiPanel] = useState<{ title: string; points: string[] } | null>(null);
  const [shareNote, setShareNote] = useState<Note | null>(null);
  const [draftTitle, setDraftTitle] = useState('');
  const [draftContent, setDraftContent] = useState('');
  const [showVersions, setShowVersions] = useState(false);
  const saveTimer = useRef<number | null>(null);

  const openNotebooks = useMemo(() => data.notebooks.filter((nb) => !nb.closed), [data.notebooks]);
  const closedNotebooks = useMemo(() => data.notebooks.filter((nb) => nb.closed), [data.notebooks]);

  const knownYears = useMemo(
    () => Array.from(new Set(data.notebooks.map((nb) => nb.year).filter(Boolean))),
    [data.notebooks],
  );

  const activeNotebook = data.notebooks.find((nb) => nb.id === activeNotebookId) || null;
  const activeNote = useMemo(() => data.notes.find((n) => n.id === activeNoteId) || null, [data.notes, activeNoteId]);

  useEffect(() => {
    if (!activeNotebookId && openNotebooks.length > 0) setActiveNotebookId(openNotebooks[0].id);
    if (activeNotebookId && !data.notebooks.some((nb) => nb.id === activeNotebookId)) setActiveNotebookId('');
  }, [openNotebooks, activeNotebookId, data.notebooks]);

  useEffect(() => {
    if (!activeNote) {
      setDraftTitle('');
      setDraftContent('');
      setShowVersions(false);
      return;
    }
    setDraftTitle(activeNote.title);
    setDraftContent(activeNote.content);
    setShowVersions(false);
  }, [activeNote?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  // Sauvegarde automatique : locale immédiate, distante après 700 ms d'inactivité.
  useEffect(() => {
    if (!activeNote) return;
    if (draftTitle === activeNote.title && draftContent === activeNote.content) return;
    if (saveTimer.current) window.clearTimeout(saveTimer.current);
    saveTimer.current = window.setTimeout(() => {
      setData((prev) => ({
        ...prev,
        notes: prev.notes.map((note) =>
          note.id === activeNote.id
            ? {
                ...note,
                title: draftTitle,
                content: draftContent,
                updatedAt: new Date().toISOString(),
              }
            : note,
        ),
      }));
    }, 700);
    return () => {
      if (saveTimer.current) window.clearTimeout(saveTimer.current);
    };
  }, [draftTitle, draftContent, activeNote, setData]);

  const notesOfNotebook = useMemo(() => {
    let list = data.notes.filter((note) => note.notebookId === activeNotebookId);
    if (search.trim()) {
      const q = search.trim().toLowerCase();
      list = list.filter(
        (note) => note.title.toLowerCase().includes(q) || note.content.toLowerCase().includes(q),
      );
    }
    if (filter === 'favorite') list = list.filter((note) => note.favorite);
    if (filter === 'important') list = list.filter((note) => note.important);
    if (filter === 'review') list = list.filter((note) => note.toReview);
    return [...list].sort((a, b) => new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime());
  }, [data.notes, activeNotebookId, search, filter]);

  const addNotebook = () => {
    const name = draftNotebook.name.trim() || draftNotebook.subject.trim();
    if (!name) return;
    const notebook = createNotebook({
      name,
      year: draftNotebook.year.trim() || String(new Date().getFullYear()),
      periodType: draftNotebook.periodType,
      periodLabel: draftNotebook.periodLabel.trim(),
      subject: draftNotebook.subject.trim() || name,
    });
    setData((prev) => ({ ...prev, notebooks: [notebook, ...prev.notebooks] }));
    setActiveNotebookId(notebook.id);
    setActiveNoteId(null);
    setDraftNotebook({
      name: '',
      subject: '',
      year: '',
      periodType: 'semester',
      periodLabel: `${t('notes.periodSemester')} 1`,
    });
    setShowNotebookForm(false);
  };

  const removeNotebook = (id: string) => {
    if (!window.confirm(t('notes.confirmDeleteNotebook'))) return;
    setData((prev) => ({
      notebooks: prev.notebooks.filter((nb) => nb.id !== id),
      notes: prev.notes.filter((note) => note.notebookId !== id),
    }));
    if (activeNotebookId === id) setActiveNotebookId('');
    if (activeNote?.notebookId === id) setActiveNoteId(null);
  };

  const toggleNotebookClosed = (id: string) => {
    setData((prev) => ({
      ...prev,
      notebooks: prev.notebooks.map((nb) => (nb.id === id ? { ...nb, closed: !nb.closed } : nb)),
    }));
    if (activeNotebookId === id) setActiveNotebookId('');
  };

  const addNote = () => {
    if (!activeNotebookId) return;
    const note = createNote(activeNotebookId);
    setData((prev) => ({ ...prev, notes: [note, ...prev.notes] }));
    setActiveNoteId(note.id);
  };

  const updateNote = (patch: Partial<Note>) => {
    if (!activeNote) return;
    setData((prev) => ({
      ...prev,
      notes: prev.notes.map((note) =>
        note.id === activeNote.id ? { ...note, ...patch, updatedAt: new Date().toISOString() } : note,
      ),
    }));
  };

  const duplicateNote = (note: Note) => {
    const copy: Note = {
      ...note,
      id: localId('note'),
      title: `${note.title || t('notes.untitled')} (copie)`,
      updatedAt: new Date().toISOString(),
      versions: [],
    };
    setData((prev) => ({ ...prev, notes: [copy, ...prev.notes] }));
    setActiveNoteId(copy.id);
  };

  const deleteNote = (note: Note) => {
    if (!window.confirm(t('notes.confirmDeleteNote'))) return;
    setData((prev) => ({ ...prev, notes: prev.notes.filter((n) => n.id !== note.id) }));
    if (activeNoteId === note.id) setActiveNoteId(null);
  };

  const saveVersion = () => {
    if (!activeNote) return;
    const version = { at: new Date().toISOString(), content: activeNote.content };
    updateNote({ versions: [version, ...(activeNote.versions || [])].slice(0, 10) });
  };

  const restoreVersion = (content: string) => {
    setDraftContent(content);
    updateNote({ content });
  };

  const exportNote = (note: Note) => {
    downloadTextFile(
      `${(note.title || 'note').replace(/[^a-z0-9]+/gi, '-').toLowerCase() || 'note'}.md`,
      `# ${note.title}\n\n${note.content}\n`,
      'text/markdown;charset=utf-8',
    );
  };

  const runAiSummary = (notes: Note[]) => {
    const points = summarizeNotes(notes);
    setAiPanel({
      title: notes.length === 1 ? notes[0].title || t('notes.untitled') : `${notes.length} ${t('notes.notesCount')}`,
      points: points.length > 0 ? points : [t('notes.summaryEmpty')],
    });
  };

  const prepareQuiz = (notes: Note[]) => {
    if (notes.length === 0) return;
    const cards = notes.flatMap((note) =>
      splitSentences(note.content)
        .slice(0, 8)
        .map((sentence) => ({ question: `Que retenir de : ${note.title || t('notes.untitled')} ?`, answer: sentence })),
    );
    if (cards.length === 0) return;
    void localSet(`user::${user?.id}::quiz_import`, {
      name: `Révision — ${notes[0].title || t('notes.untitled')}`,
      cards,
      createdAt: new Date().toISOString(),
    });
    navigate('/mon-compte/apps/zifek-flashcards');
  };

  const toggleSelected = (id: string) => {
    setSelectedNoteIds((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));
  };

  const selectedNotes = data.notes.filter((note) => selectedNoteIds.includes(note.id));

  const notebookCard = (notebook: (typeof data.notebooks)[number]) => {
    const count = data.notes.filter((note) => note.notebookId === notebook.id).length;
    return (
      <div
        key={notebook.id}
        className={`group relative rounded-lg border p-3 min-h-[124px] flex flex-col transition-colors ${
          activeNotebookId === notebook.id && !notebook.closed
            ? 'border-accent-300 bg-accent-50/60'
            : 'border-background-200/60 bg-background-50 hover:border-foreground-300/60'
        }`}
      >
        <button
          onClick={() => {
            setActiveNotebookId(notebook.id);
            setActiveNoteId(null);
            setSearch('');
            setFilter('all');
            setSelectionMode(false);
            setSelectedNoteIds([]);
          }}
          className="text-left flex-1 cursor-pointer"
        >
          <span className={`w-8 h-8 rounded-md ${notebook.color || NOTEBOOK_COLORS[0]} flex items-center justify-center mb-2`}>
            <i className={`${periodIcon(notebook.periodType)} text-sm`}></i>
          </span>
          <p className="text-xs font-semibold text-foreground-900 leading-snug line-clamp-2">{notebook.name}</p>
          <p className="text-[10px] text-foreground-500 mt-0.5 truncate">{notebook.subject}</p>
          <p className="text-[10px] text-foreground-400 mt-1 truncate">
            {notebook.year} · {notebook.periodLabel}
          </p>
        </button>
        <div className="flex items-center justify-between mt-2">
          <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-background-100 text-foreground-600 font-medium whitespace-nowrap">
            {count} {count === 1 ? t('notes.noteCount') : t('notes.notesCount')}
          </span>
          <div className="flex items-center gap-0.5">
            <button
              onClick={() => toggleNotebookClosed(notebook.id)}
              className="w-6 h-6 rounded flex items-center justify-center text-foreground-400 hover:text-accent-700 hover:bg-accent-100/60 cursor-pointer"
              title={notebook.closed ? t('notes.reopenNotebook') : t('notes.closeNotebook')}
            >
              <i className={notebook.closed ? 'ri-folder-open-line text-[11px]' : 'ri-archive-line text-[11px]'}></i>
            </button>
            <button
              onClick={() => removeNotebook(notebook.id)}
              className="w-6 h-6 rounded flex items-center justify-center text-foreground-400 hover:text-red-600 hover:bg-red-50 cursor-pointer"
              title={t('notes.deleteNotebook')}
            >
              <i className="ri-delete-bin-line text-[11px]"></i>
            </button>
          </div>
        </div>
      </div>
    );
  };

  return (
    <AppShell
      slug="zifek-notes"
      sync={{
        offline: storage.offline,
        syncing: storage.syncing,
        pending: storage.pending,
        syncError: storage.syncError,
        onSync: storage.forceSync,
      }}
      headerExtra={
        <button
          onClick={() => {
            setSelectionMode((v) => !v);
            setSelectedNoteIds([]);
          }}
          className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-[11px] font-medium whitespace-nowrap cursor-pointer transition-colors ${
            selectionMode
              ? 'bg-foreground-950 text-background-50'
              : 'bg-background-100 text-foreground-600 hover:bg-background-200/70'
          }`}
        >
          <i className="ri-checkbox-multiple-line"></i>
          <span className="hidden sm:inline">{t('notes.selection')}</span>
        </button>
      }
    >
      {!ready ? (
        <div className="flex items-center justify-center py-20">
          <i className="ri-loader-4-line animate-spin text-2xl text-foreground-400"></i>
        </div>
      ) : (
        <div className={`space-y-4 ${isRtl ? 'text-right' : ''}`} dir={isRtl ? 'rtl' : 'ltr'}>
          {/* En-tête */}
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
            <div className="min-w-0">
              <h2 className="text-base md:text-lg font-bold font-heading text-foreground-950">{t('notes.notebooks')}</h2>
              <p className="text-[11px] md:text-xs text-foreground-500 mt-0.5">{t('notes.subtitle')}</p>
            </div>
            <button
              onClick={() => setShowNotebookForm((v) => !v)}
              className="flex items-center justify-center gap-1.5 px-4 py-2.5 rounded-full bg-foreground-950 text-background-50 text-xs font-semibold whitespace-nowrap cursor-pointer hover:bg-foreground-800"
            >
              <i className="ri-add-line"></i>
              {t('notes.newNotebook')}
            </button>
          </div>

          {/* Formulaire carnet */}
          {showNotebookForm && (
            <div className="bg-background-50 border border-background-200/60 rounded-xl p-3.5 md:p-4">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5">
                <input
                  value={draftNotebook.name}
                  onChange={(e) => setDraftNotebook({ ...draftNotebook, name: e.target.value })}
                  placeholder={t('notes.name')}
                  className="px-3 py-2.5 rounded-md border border-background-200/70 bg-background-50 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-foreground-300"
                />
                <input
                  value={draftNotebook.subject}
                  onChange={(e) => setDraftNotebook({ ...draftNotebook, subject: e.target.value })}
                  placeholder={t('notes.subject')}
                  className="px-3 py-2.5 rounded-md border border-background-200/70 bg-background-50 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-foreground-300"
                />
                <div className="sm:col-span-2">
                  <input
                    value={draftNotebook.year}
                    onChange={(e) => setDraftNotebook({ ...draftNotebook, year: e.target.value })}
                    placeholder={t('notes.year')}
                    className="w-full px-3 py-2.5 rounded-md border border-background-200/70 bg-background-50 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-foreground-300"
                  />
                  <div className="flex flex-wrap gap-1.5 mt-1.5">
                    {Array.from(new Set([...knownYears, `${new Date().getFullYear()}–${new Date().getFullYear() + 1}`])).map((year) => (
                      <button
                        key={year}
                        onClick={() => setDraftNotebook({ ...draftNotebook, year })}
                        className="px-2 py-1 rounded-full bg-background-100 text-foreground-600 text-[10px] font-medium whitespace-nowrap cursor-pointer hover:bg-background-200/70"
                      >
                        {year}
                      </button>
                    ))}
                  </div>
                </div>
                <select
                  value={draftNotebook.periodType}
                  onChange={(e) => {
                    const type = e.target.value as PeriodType;
                    const label =
                      type === 'semester'
                        ? `${t('notes.periodSemester')} 1`
                        : type === 'trimester'
                          ? `${t('notes.periodTrimester')} 1`
                          : type === 'year'
                            ? t('notes.periodYear')
                            : '';
                    setDraftNotebook({ ...draftNotebook, periodType: type, periodLabel: label });
                  }}
                  className="px-3 py-2.5 rounded-md border border-background-200/70 bg-background-50 text-sm text-foreground-900 focus:outline-none focus:border-foreground-300 cursor-pointer"
                >
                  <option value="semester">{t('notes.periodSemester')}</option>
                  <option value="trimester">{t('notes.periodTrimester')}</option>
                  <option value="year">{t('notes.periodYear')}</option>
                  <option value="other">{t('notes.periodOther')}</option>
                </select>
                <input
                  value={draftNotebook.periodLabel}
                  onChange={(e) => setDraftNotebook({ ...draftNotebook, periodLabel: e.target.value })}
                  placeholder={t('notes.periodLabelHint')}
                  className="px-3 py-2.5 rounded-md border border-background-200/70 bg-background-50 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-foreground-300"
                />
              </div>
              <div className="flex items-center justify-end gap-2 mt-3">
                <button
                  onClick={() => setShowNotebookForm(false)}
                  className="px-3.5 py-2 rounded-full text-xs font-medium text-foreground-600 hover:bg-background-100 cursor-pointer whitespace-nowrap"
                >
                  {t('app.cancel')}
                </button>
                <button
                  onClick={addNotebook}
                  className="px-4 py-2 rounded-full bg-accent-500 text-background-50 text-xs font-semibold whitespace-nowrap cursor-pointer hover:bg-accent-600"
                >
                  {t('app.create')}
                </button>
              </div>
            </div>
          )}

          {/* Grille des carnets */}
          {openNotebooks.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-14 px-6 text-center border border-dashed border-background-300/70 rounded-xl">
              <div className="w-12 h-12 rounded-xl bg-background-100 flex items-center justify-center mb-3">
                <i className="ri-book-2-line text-lg text-foreground-400"></i>
              </div>
              <p className="text-sm text-foreground-600">{t('notes.emptyNotebooks')}</p>
              <button
                onClick={() => setShowNotebookForm(true)}
                className="mt-3 px-4 py-2 rounded-full bg-foreground-950 text-background-50 text-xs font-medium whitespace-nowrap cursor-pointer"
              >
                {t('notes.newNotebook')}
              </button>
            </div>
          ) : (
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-2.5">
              {openNotebooks.map(notebookCard)}
            </div>
          )}

          {/* Carnets fermés */}
          {closedNotebooks.length > 0 && (
            <div>
              <button
                onClick={() => setShowClosed((v) => !v)}
                className="flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wider text-foreground-500 hover:text-foreground-800 cursor-pointer"
              >
                <i className={showClosed ? 'ri-arrow-down-s-line' : 'ri-arrow-right-s-line'}></i>
                {t('notes.closed')} ({closedNotebooks.length})
              </button>
              {showClosed && (
                <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-2.5 mt-2">
                  {closedNotebooks.map(notebookCard)}
                </div>
              )}
            </div>
          )}

          {/* Carnet ouvert : notes */}
          {activeNotebook && !activeNote && (
            <div className="bg-background-50 border border-background-200/60 rounded-xl overflow-hidden">
              <div className="flex flex-wrap items-center gap-2 px-3.5 py-3 border-b border-background-200/50">
                <div className="min-w-0 flex-1">
                  <p className="text-sm font-semibold text-foreground-900 truncate">{activeNotebook.name}</p>
                  <p className="text-[11px] text-foreground-500 truncate">
                    {activeNotebook.year} · {activeNotebook.periodLabel} · {activeNotebook.subject}
                  </p>
                </div>
                <button
                  onClick={addNote}
                  className="flex items-center gap-1 px-3 py-1.5 rounded-full bg-foreground-950 text-background-50 text-[11px] font-medium whitespace-nowrap cursor-pointer hover:bg-foreground-800"
                >
                  <i className="ri-add-line"></i>
                  {t('notes.newNote')}
                </button>
                <button
                  onClick={() => {
                    setActiveNotebookId('');
                    setActiveNoteId(null);
                  }}
                  className="w-8 h-8 rounded-full flex items-center justify-center text-foreground-500 hover:bg-background-100 cursor-pointer"
                  title={t('app.back')}
                >
                  <i className="ri-close-line"></i>
                </button>
              </div>

              <div className="px-3.5 py-2.5 border-b border-background-200/50 flex flex-col sm:flex-row gap-2">
                <div className="relative flex-1">
                  <i className="ri-search-line absolute left-2.5 top-1/2 -translate-y-1/2 text-foreground-400 text-xs"></i>
                  <input
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    placeholder={t('app.search')}
                    className="w-full pl-8 pr-3 py-2 rounded-md border border-background-200/70 bg-background-50 text-xs text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-foreground-300"
                  />
                </div>
                <div className="flex items-center gap-1 flex-wrap">
                  {[
                    { key: 'all', label: t('notes.all') },
                    { key: 'favorite', label: t('notes.favorites') },
                    { key: 'important', label: t('notes.important') },
                    { key: 'review', label: t('notes.toReview') },
                  ].map((item) => (
                    <button
                      key={item.key}
                      onClick={() => setFilter(item.key as typeof filter)}
                      className={`px-2.5 py-1 rounded-full text-[10px] font-medium whitespace-nowrap cursor-pointer transition-colors ${
                        filter === item.key
                          ? 'bg-foreground-950 text-background-50'
                          : 'bg-background-100 text-foreground-600 hover:bg-background-200/70'
                      }`}
                    >
                      {item.label}
                    </button>
                  ))}
                </div>
              </div>

              {notesOfNotebook.length === 0 ? (
                <div className="py-12 px-6 text-center">
                  <i className="ri-sticky-note-line text-2xl text-foreground-300"></i>
                  <p className="text-xs text-foreground-500 mt-2">{t('notes.emptyNotes')}</p>
                </div>
              ) : (
                <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-2.5 p-3">
                  {notesOfNotebook.map((note) => (
                    <div
                      key={note.id}
                      onClick={() => (selectionMode ? toggleSelected(note.id) : setActiveNoteId(note.id))}
                      className={`group relative rounded-lg border p-3 min-h-[112px] cursor-pointer transition-colors ${
                        selectedNoteIds.includes(note.id)
                          ? 'border-accent-400 bg-accent-50/70'
                          : 'border-background-200/60 bg-background-50 hover:border-foreground-300/60'
                      }`}
                    >
                      <div className="flex items-start gap-1.5 mb-1">
                        {selectionMode && (
                          <input
                            type="checkbox"
                            checked={selectedNoteIds.includes(note.id)}
                            onChange={() => toggleSelected(note.id)}
                            onClick={(e) => e.stopPropagation()}
                            className="mt-0.5 w-3.5 h-3.5 accent-accent-600 cursor-pointer flex-shrink-0"
                          />
                        )}
                        <p className="text-xs font-semibold text-foreground-900 line-clamp-2 flex-1">
                          {note.title || t('notes.untitled')}
                        </p>
                      </div>
                      <p className="text-[10px] text-foreground-500 line-clamp-3 leading-relaxed">
                        {note.content || '—'}
                      </p>
                      <div className="flex items-center gap-1 mt-1.5">
                        {note.favorite && <i className="ri-star-fill text-[9px] text-accent-500"></i>}
                        {note.important && <i className="ri-error-warning-fill text-[9px] text-primary-600"></i>}
                        {note.toReview && <i className="ri-refresh-line text-[9px] text-secondary-700"></i>}
                        <span className="text-[9px] text-foreground-400 ml-auto">{formatDateTime(note.updatedAt)}</span>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Barre de sélection IA */}
          {selectionMode && activeNotebook && !activeNote && (
            <div className="sticky bottom-2 z-10 flex flex-wrap items-center gap-2 px-3 py-2.5 rounded-xl bg-foreground-950 text-background-50">
              <span className="text-[11px] flex-1">{selectedNoteIds.length} {t('notes.selected')}</span>
              <button
                onClick={() => runAiSummary(selectedNotes)}
                disabled={selectedNotes.length === 0}
                className="flex items-center gap-1 px-3 py-1.5 rounded-full bg-accent-500 text-background-50 text-[11px] font-semibold whitespace-nowrap cursor-pointer disabled:opacity-40"
              >
                <i className="ri-sparkling-line"></i>
                {t('notes.summary')}
              </button>
              <button
                onClick={() => prepareQuiz(selectedNotes)}
                disabled={selectedNotes.length === 0}
                className="flex items-center gap-1 px-3 py-1.5 rounded-full bg-background-50/15 text-background-50 text-[11px] font-medium whitespace-nowrap cursor-pointer disabled:opacity-40"
              >
                <i className="ri-stack-line"></i>
                {t('notes.makeQuiz')}
              </button>
            </div>
          )}

          {/* Éditeur */}
          {activeNote && (
            <div className="bg-background-50 border border-background-200/60 rounded-xl flex flex-col min-h-[420px]">
              <div className="flex flex-wrap items-center gap-1 px-2.5 py-2 border-b border-background-200/50">
                <button
                  onClick={() => setActiveNoteId(null)}
                  className="w-8 h-8 rounded-full flex items-center justify-center text-foreground-600 hover:bg-background-100 cursor-pointer"
                  title={t('app.back')}
                >
                  <i className={isRtl ? 'ri-arrow-right-line' : 'ri-arrow-left-line'}></i>
                </button>
                <button
                  onClick={() => updateNote({ favorite: !activeNote.favorite })}
                  className={`w-8 h-8 rounded-full flex items-center justify-center cursor-pointer ${
                    activeNote.favorite ? 'bg-accent-100 text-accent-700' : 'text-foreground-400 hover:bg-background-100'
                  }`}
                  title={t('notes.favorites')}
                >
                  <i className="ri-star-line text-sm"></i>
                </button>
                <button
                  onClick={() => updateNote({ important: !activeNote.important })}
                  className={`w-8 h-8 rounded-full flex items-center justify-center cursor-pointer ${
                    activeNote.important ? 'bg-primary-100 text-primary-700' : 'text-foreground-400 hover:bg-background-100'
                  }`}
                  title={t('notes.important')}
                >
                  <i className="ri-error-warning-line text-sm"></i>
                </button>
                <button
                  onClick={() => updateNote({ toReview: !activeNote.toReview })}
                  className={`w-8 h-8 rounded-full flex items-center justify-center cursor-pointer ${
                    activeNote.toReview ? 'bg-secondary-100 text-secondary-900' : 'text-foreground-400 hover:bg-background-100'
                  }`}
                  title={t('notes.toReview')}
                >
                  <i className="ri-refresh-line text-sm"></i>
                </button>
                <span className="w-px h-5 bg-background-200/70 mx-0.5"></span>
                <button
                  onClick={() => runAiSummary([activeNote])}
                  className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-full bg-background-100 text-foreground-700 text-[11px] font-medium whitespace-nowrap cursor-pointer hover:bg-background-200/70"
                >
                  <i className="ri-sparkling-line"></i>
                  <span className="hidden sm:inline">{t('notes.askAi')}</span>
                </button>
                <button
                  onClick={() => setShareNote(activeNote)}
                  className="w-8 h-8 rounded-full flex items-center justify-center text-foreground-400 hover:bg-background-100 cursor-pointer"
                  title={t('notes.share')}
                >
                  <i className="ri-share-line text-sm"></i>
                </button>
                <button
                  onClick={() => duplicateNote(activeNote)}
                  className="w-8 h-8 rounded-full flex items-center justify-center text-foreground-400 hover:bg-background-100 cursor-pointer"
                  title={t('notes.duplicate')}
                >
                  <i className="ri-file-copy-line text-sm"></i>
                </button>
                <button
                  onClick={() => exportNote(activeNote)}
                  className="w-8 h-8 rounded-full flex items-center justify-center text-foreground-400 hover:bg-background-100 cursor-pointer"
                  title={t('notes.export')}
                >
                  <i className="ri-download-2-line text-sm"></i>
                </button>
                <button
                  onClick={() => setShowVersions((v) => !v)}
                  className="w-8 h-8 rounded-full flex items-center justify-center text-foreground-400 hover:bg-background-100 cursor-pointer"
                  title={t('notes.versions')}
                >
                  <i className="ri-history-line text-sm"></i>
                </button>
                <button
                  onClick={() => deleteNote(activeNote)}
                  className="w-8 h-8 rounded-full flex items-center justify-center text-foreground-400 hover:text-red-600 hover:bg-red-50 cursor-pointer ml-auto"
                  title={t('app.delete')}
                >
                  <i className="ri-delete-bin-line text-sm"></i>
                </button>
              </div>

              {showVersions && (
                <div className="px-3.5 py-3 border-b border-background-200/50 bg-background-100/40">
                  <div className="flex items-center justify-between gap-2 mb-2">
                    <span className="text-[11px] font-semibold text-foreground-700">{t('notes.versions')}</span>
                    <button
                      onClick={saveVersion}
                      className="px-2.5 py-1 rounded-full bg-background-50 border border-background-200/70 text-[10px] font-medium text-foreground-700 cursor-pointer whitespace-nowrap"
                    >
                      {t('notes.saveVersion')}
                    </button>
                  </div>
                  {(activeNote.versions || []).length === 0 ? (
                    <p className="text-[11px] text-foreground-400">{t('notes.noVersions')}</p>
                  ) : (
                    <ul className="space-y-1 max-h-32 overflow-y-auto">
                      {(activeNote.versions || []).map((version) => (
                        <li key={version.at} className="flex items-center gap-2 text-[11px]">
                          <span className="text-foreground-500 flex-1">{formatDateTime(version.at)}</span>
                          <button
                            onClick={() => restoreVersion(version.content)}
                            className="text-primary-700 font-medium cursor-pointer whitespace-nowrap"
                          >
                            {t('notes.restore')}
                          </button>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              )}

              <input
                value={draftTitle}
                onChange={(e) => setDraftTitle(e.target.value)}
                placeholder={t('notes.untitled')}
                className="w-full px-4 pt-3.5 pb-1.5 bg-transparent text-base md:text-lg font-bold font-heading text-foreground-950 placeholder:text-foreground-300 focus:outline-none"
              />
              <textarea
                value={draftContent}
                onChange={(e) => setDraftContent(e.target.value)}
                placeholder={t('notes.placeholder')}
                className="flex-1 w-full px-4 pb-3.5 bg-transparent text-sm leading-relaxed text-foreground-700 placeholder:text-foreground-300 focus:outline-none resize-none min-h-[280px]"
              />
              <div className="px-4 py-2 border-t border-background-200/50 flex items-center justify-between text-[10px] text-foreground-400">
                <span>{draftContent.trim().split(/\s+/).filter(Boolean).length} {t('notes.words')}</span>
                <span>{t('notes.autosave')}</span>
              </div>
            </div>
          )}

          {!activeNotebook && openNotebooks.length > 0 && !activeNote && (
            <p className="text-[11px] text-foreground-400 text-center py-2">{t('notes.selectNotebook')}</p>
          )}
        </div>
      )}

      {/* Résumé IA */}
      {aiPanel && (
        <div className="fixed inset-0 z-[80] flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-foreground-950/45" onClick={() => setAiPanel(null)} aria-hidden="true" />
          <div className="relative w-full max-w-lg bg-background-50 rounded-xl p-5 max-h-[85vh] overflow-y-auto">
            <div className="flex items-start justify-between gap-3 mb-3">
              <div className="flex items-center gap-2 min-w-0">
                <span className="w-8 h-8 rounded-lg bg-accent-100 text-accent-800 flex items-center justify-center flex-shrink-0">
                  <i className="ri-sparkling-line text-sm"></i>
                </span>
                <div className="min-w-0">
                  <h3 className="text-sm font-semibold text-foreground-950">{t('notes.summary')}</h3>
                  <p className="text-[11px] text-foreground-500 truncate">{aiPanel.title}</p>
                </div>
              </div>
              <button
                onClick={() => setAiPanel(null)}
                className="w-8 h-8 rounded-full flex items-center justify-center text-foreground-500 hover:bg-background-100 cursor-pointer flex-shrink-0"
                title={t('app.close')}
              >
                <i className="ri-close-line"></i>
              </button>
            </div>
            <ul className="space-y-2">
              {aiPanel.points.map((point, index) => (
                <li key={index} className="flex items-start gap-2 text-sm text-foreground-700 leading-relaxed">
                  <span className="w-5 h-5 rounded-full bg-background-100 text-[10px] font-bold text-foreground-600 flex items-center justify-center flex-shrink-0 mt-0.5">
                    {index + 1}
                  </span>
                  {point}
                </li>
              ))}
            </ul>
            <div className="flex items-center justify-end gap-2 mt-4">
              <button
                onClick={() => {
                  prepareQuiz(selectedNotes.length > 0 ? selectedNotes : activeNote ? [activeNote] : []);
                  setAiPanel(null);
                }}
                className="px-4 py-2 rounded-full bg-background-100 text-foreground-700 text-xs font-medium whitespace-nowrap cursor-pointer"
              >
                {t('notes.makeQuiz')}
              </button>
              <button
                onClick={() => setAiPanel(null)}
                className="px-4 py-2 rounded-full bg-foreground-950 text-background-50 text-xs font-medium whitespace-nowrap cursor-pointer"
              >
                {t('app.close')}
              </button>
            </div>
          </div>
        </div>
      )}

      {shareNote && (
        <ShareItemModal
          kind="note"
          title={shareNote.title || t('notes.untitled')}
          subtitle={activeNotebook?.name}
          text={shareNote.content}
          onClose={() => setShareNote(null)}
        />
      )}
    </AppShell>
  );
}